import type { ChordTone, NoteName } from "./chords";

const NOTE_OFFSETS: Record<NoteName, number> = {
  C: 0,
  "C#": 1,
  D: 2,
  "D#": 3,
  E: 4,
  F: 5,
  "F#": 6,
  G: 7,
  "G#": 8,
  A: 9,
  "A#": 10,
  B: 11,
};

let audioContext: AudioContext | undefined;
let leadVoice: { oscillator: OscillatorNode; gain: GainNode } | undefined;
let heldChordVoice: { output: GainNode; voices: Array<{ oscillator: OscillatorNode; gain: GainNode }> } | undefined;

export async function playChord(tones: ChordTone[], baseOctave = 4) {
  const context = getAudioContext();

  if (context.state === "suspended") {
    await context.resume();
  }

  const now = context.currentTime;
  stopHeldChord(now);
  stopLeadVoice(now);

  const output = context.createGain();
  output.gain.setValueAtTime(0.0001, now);
  output.gain.exponentialRampToValueAtTime(0.16, now + 0.025);
  output.gain.exponentialRampToValueAtTime(0.075, now + 0.22);
  output.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
  output.connect(context.destination);

  tones.forEach(tone => {
    const oscillator = context.createOscillator();
    const voiceGain = context.createGain();

    oscillator.type = "sawtooth";
    oscillator.frequency.setValueAtTime(toneToFrequency(tone, baseOctave), now);
    voiceGain.gain.setValueAtTime(1 / Math.sqrt(Math.max(tones.length, 1)), now);

    oscillator.connect(voiceGain);
    voiceGain.connect(output);
    oscillator.start(now);
    oscillator.stop(now + 1.25);
  });

  window.setTimeout(() => output.disconnect(), 1400);
}

export async function playStrum(tones: ChordTone[], baseOctave = 4) {
  const context = getAudioContext();

  if (context.state === "suspended") {
    await context.resume();
  }

  const now = context.currentTime;
  const strumGap = 0.055;
  const lastStart = Math.max(tones.length - 1, 0) * strumGap;
  stopHeldChord(now);
  stopLeadVoice(now);

  const output = context.createGain();
  output.gain.setValueAtTime(0.0001, now);
  output.gain.exponentialRampToValueAtTime(0.16, now + 0.025);
  output.gain.exponentialRampToValueAtTime(0.075, now + lastStart + 0.22);
  output.gain.exponentialRampToValueAtTime(0.0001, now + lastStart + 1.2);
  output.connect(context.destination);

  tones.forEach((tone, index) => {
    const startAt = now + index * strumGap;
    const oscillator = context.createOscillator();
    const voiceGain = context.createGain();

    oscillator.type = "sawtooth";
    oscillator.frequency.setValueAtTime(toneToFrequency(tone, baseOctave), startAt);
    voiceGain.gain.setValueAtTime(0.0001, now);
    voiceGain.gain.setValueAtTime(0.0001, startAt);
    voiceGain.gain.exponentialRampToValueAtTime(1 / Math.sqrt(Math.max(tones.length, 1)), startAt + 0.015);

    oscillator.connect(voiceGain);
    voiceGain.connect(output);
    oscillator.start(startAt);
    oscillator.stop(now + lastStart + 1.25);
  });

  window.setTimeout(() => output.disconnect(), (lastStart + 1.4) * 1000);
}

export async function playLeadTone(tone: ChordTone, baseOctave = 4) {
  const context = getAudioContext();

  if (context.state === "suspended") {
    await context.resume();
  }

  stopHeldChord(context.currentTime);
  stopLeadVoice(context.currentTime);

  const now = context.currentTime;
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = "sawtooth";
  oscillator.frequency.setValueAtTime(toneToFrequency(tone, baseOctave), now);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.16, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.075, now + 0.18);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.1);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(now);
  oscillator.stop(now + 1.15);

  leadVoice = { oscillator, gain };
  const currentVoice = leadVoice;
  oscillator.onended = () => {
    gain.disconnect();

    if (leadVoice === currentVoice) {
      leadVoice = undefined;
    }
  };
}

export async function startHeldChord(tones: ChordTone[], baseOctave = 4) {
  const context = getAudioContext();

  if (context.state === "suspended") {
    await context.resume();
  }

  const now = context.currentTime;
  stopHeldChord(now);
  stopLeadVoice(now);

  const output = context.createGain();
  const voiceLevel = 1 / Math.sqrt(Math.max(tones.length, 1));
  const voices = tones.map(tone => createHeldVoice(context, output, tone, baseOctave, voiceLevel, now));

  output.gain.setValueAtTime(0.0001, now);
  output.gain.exponentialRampToValueAtTime(0.16, now + 0.025);
  output.gain.exponentialRampToValueAtTime(0.075, now + 0.22);
  output.connect(context.destination);

  heldChordVoice = { output, voices };
}

export async function morphHeldChord(tones: ChordTone[], baseOctave = 4) {
  if (!heldChordVoice) {
    await startHeldChord(tones, baseOctave);
    return;
  }

  const context = getAudioContext();

  if (context.state === "suspended") {
    await context.resume();
  }

  const now = context.currentTime;
  const morphTime = 0.045;
  const voiceLevel = 1 / Math.sqrt(Math.max(tones.length, 1));
  const existingVoices = heldChordVoice.voices;

  tones.forEach((tone, index) => {
    const existingVoice = existingVoices[index];

    if (existingVoice) {
      existingVoice.oscillator.frequency.cancelScheduledValues(now);
      existingVoice.oscillator.frequency.setValueAtTime(existingVoice.oscillator.frequency.value, now);
      existingVoice.oscillator.frequency.exponentialRampToValueAtTime(toneToFrequency(tone, baseOctave), now + morphTime);
      existingVoice.gain.gain.cancelScheduledValues(now);
      existingVoice.gain.gain.setValueAtTime(Math.max(existingVoice.gain.gain.value, 0.0001), now);
      existingVoice.gain.gain.exponentialRampToValueAtTime(voiceLevel, now + morphTime);
      return;
    }

    existingVoices.push(createHeldVoice(context, heldChordVoice!.output, tone, baseOctave, voiceLevel, now, morphTime));
  });

  for (let index = tones.length; index < existingVoices.length; index += 1) {
    fadeOutHeldVoice(existingVoices[index]!, now, morphTime);
  }

  heldChordVoice.voices = existingVoices.slice(0, tones.length);
}

export function releaseHeldChord() {
  if (!audioContext) {
    return;
  }

  stopHeldChord(audioContext.currentTime, 0.35);
}

function getAudioContext() {
  audioContext ??= new AudioContext();
  return audioContext;
}

function createHeldVoice(
  context: AudioContext,
  output: GainNode,
  tone: ChordTone,
  baseOctave: number,
  voiceLevel: number,
  now: number,
  fadeInTime = 0,
) {
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = "sawtooth";
  oscillator.frequency.setValueAtTime(toneToFrequency(tone, baseOctave), now);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(voiceLevel, now + Math.max(fadeInTime, 0.001));
  oscillator.connect(gain);
  gain.connect(output);
  oscillator.start(now);

  return { oscillator, gain };
}

function fadeOutHeldVoice(voice: { oscillator: OscillatorNode; gain: GainNode }, now: number, releaseTime: number) {
  voice.gain.gain.cancelScheduledValues(now);
  voice.gain.gain.setValueAtTime(Math.max(voice.gain.gain.value, 0.0001), now);
  voice.gain.gain.exponentialRampToValueAtTime(0.0001, now + releaseTime);
  voice.oscillator.stop(now + releaseTime + 0.01);
  window.setTimeout(() => voice.gain.disconnect(), (releaseTime + 0.08) * 1000);
}

function stopHeldChord(now: number, releaseTime = 0.025) {
  if (!heldChordVoice) {
    return;
  }

  const stoppedVoice = heldChordVoice;
  stoppedVoice.output.gain.cancelScheduledValues(now);
  stoppedVoice.output.gain.setValueAtTime(Math.max(stoppedVoice.output.gain.value, 0.0001), now);
  stoppedVoice.output.gain.exponentialRampToValueAtTime(0.0001, now + releaseTime);

  stoppedVoice.voices.forEach(voice => fadeOutHeldVoice(voice, now, releaseTime));
  window.setTimeout(() => stoppedVoice.output.disconnect(), (releaseTime + 0.08) * 1000);
  heldChordVoice = undefined;
}

function stopLeadVoice(now: number) {
  if (!leadVoice) {
    return;
  }

  const stoppedVoice = leadVoice;
  stoppedVoice.gain.gain.cancelScheduledValues(now);
  stoppedVoice.gain.gain.setValueAtTime(Math.max(stoppedVoice.gain.gain.value, 0.0001), now);
  stoppedVoice.gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.025);
  stoppedVoice.oscillator.stop(now + 0.03);
  window.setTimeout(() => stoppedVoice.gain.disconnect(), 60);
  leadVoice = undefined;
}

function toneToFrequency(tone: ChordTone, baseOctave: number) {
  return noteToFrequency(tone.note, baseOctave + Math.floor(tone.semitoneOffset / 12));
}

function noteToFrequency(note: NoteName, octave: number) {
  const midi = 12 * (octave + 1) + NOTE_OFFSETS[note];
  return 440 * 2 ** ((midi - 69) / 12);
}
